<template>
  <v-app id="wgu-app" data-app :class="{ 'wgu-app': true, 'wgu-app-embedded': isEmbedded }">

    <slot name="wgu-app-begin" />

    <wgu-app-header :color="baseColor">
      <!-- forward the slots of AppHeader -->
      <slot name="wgu-tb-start" slot="wgu-tb-start" />
      <slot name="wgu-tb-after-title" slot="wgu-tb-after-title" />
      <slot name="wgu-tb-before-auto-buttons" slot="wgu-tb-before-auto-buttons" />
      <slot name="wgu-tb-after-auto-buttons" slot="wgu-tb-after-auto-buttons" />
      <slot name="wgu-tb-end" slot="wgu-tb-end" />
    </wgu-app-header>

    <slot name="wgu-after-header" />

    <wgu-app-logo />

    <slot name="wgu-before-content" />

    <v-content>
      <template v-for="(modulePanel, index) in modulePanels">
        <component
          :is="modulePanel.type" :key="index" :ref="modulePanel.type"
          :color="baseColor"
          :dark="modulePanel.dark"
          :active="modulePanel.active"
          :right="modulePanel.right"
          :width="modulePanel.width"
          :height="modulePanel.height"
          :title="modulePanel.title"
          :icon="modulePanel.icon"
          :toggleGroup="modulePanel.toggleGroup"
          :options="modulePanel.options"
        />
      </template>

      <v-container id="ol-map-container" fluid fill-height style="padding: 0">
         <wgu-map :color="baseColor" />
         <!-- layer loading indicator -->
         <wgu-maploading-status :color="baseColor" />
         <slot name="wgu-after-map" />
      </v-container>
    </v-content>

    <template v-for="(moduleWin, index) in moduleWins">
      <component
        :is="moduleWin.type" :key="index" :ref="moduleWin.type"
        :color="baseColor"
        :dark="moduleWin.dark"
        :draggable="moduleWin.draggable"
        :initPos="moduleWin.initPos"
        :active="moduleWin.active"
        :width="moduleWin.width"
        :title="moduleWin.title"
        :icon="moduleWin.icon"
        :options="moduleWin.options"
      />
    </template>

    <slot name="wgu-before-footer" />

    <wgu-app-footer
      :color="baseColor"
      :footerTextLeft="footerTextLeft"
      :footerTextRight="footerTextRight"
      :showCopyrightYear="showCopyrightYear"
    />

    <slot name="wgu-after-footer" />

    <slot name="wgu-app-end" />

  </v-app>
</template>

<script>
  import Vue from 'vue'
  import { WguEventBus } from '../src/WguEventBus'
  import OlMap from '../src/components/ol/Map'
  import AppHeader from './components/AppHeader'
  import AppFooter from './components/AppFooter'
  import AppLogo from '../src/components/AppLogo'
  import MeasureWin from '../src/components/measuretool/MeasureWin'
  import LayerListPanel from './components/layerlist/LayerListPanel'
  import InfoClickWin from '../src/components/infoclick/InfoClickWin'
  import MapLoadingStatus from '../src/components/progress/MapLoadingStatus'
  import FeatureInfoPanel from './components/featureinfo/FeatureInfoPanel'
  import RoutingPanel from '../src/components/routing/RoutingPanel'
  export default {
    name: 'wgu-app-tpl',
    components: {
      'wgu-map': OlMap,
      'wgu-app-header': AppHeader,
      'wgu-app-footer': AppFooter,
      'wgu-app-logo': AppLogo,
      'wgu-measuretool-win': MeasureWin,
      'wgu-infoclick-win': InfoClickWin,
      'wgu-maploading-status': MapLoadingStatus,
      'wgu-feature-info-panel': FeatureInfoPanel,
      'wgu-layerlist-panel': LayerListPanel,
      'wgu-routing-panel': RoutingPanel
    },
    data () {
      return {
        isEmbedded: false,
        moduleWins: this.getModuleWinData(),
        modulePanels: this.getModulePanelData(),
        footerTextLeft: Vue.prototype.$appConfig.footerTextLeft,
        footerTextRight: Vue.prototype.$appConfig.footerTextRight,
        showCopyrightYear: Vue.prototype.$appConfig.showCopyrightYear,
        baseColor: Vue.prototype.$appConfig.baseColor
      }
    },
    mounted () {
      // apply the isEmbedded state to the member var
      this.isEmbedded = this.$isEmbedded;

      // make the refs (floating module window, which are not connected to their
      // related components, e.g. buttons to toggle them)
      const refs = this.$refs;
      let cmpLookup = {};
      for (const key of Object.keys(refs)) {
        cmpLookup[key] = refs[key][0];
      }
      Vue.prototype.cmpLookup = cmpLookup;
      // inform registered cmps that the app is mounted and the dynamic
      // components are available
      WguEventBus.$emit('app-mounted');
    },
    methods: {
      /**
       * Determines the module panel configuration objects from app-config:
       *     modulePanels: [
       *       {type: 'wgu-layerlist-win'},
       *       {type: 'wgu-measuretool-win'}
       *     ]
       * @return {Array} module panel configuration objects
       */
      getModulePanelData () {
        const appConfig = Vue.prototype.$appConfig || {};
        const modulesConfs = appConfig.modules || {};
        let modulePanels = [];
        for (const key of Object.keys(modulesConfs)) {
          const moduleOpts = appConfig.modules[key];
          if (moduleOpts.panel === true) {
            modulePanels.push({
              type: key + '-panel',
              dark: moduleOpts.darkLayout,
              initPos: moduleOpts.initPos,
              active: moduleOpts.mobileActive !== undefined && window.innerWidth <= 800 ? moduleOpts.mobileActive : moduleOpts.active,
              title: moduleOpts.title,
              right: moduleOpts.right,
              width: moduleOpts.width,
              height: moduleOpts.height,
              icon: moduleOpts.icon,
              toggleGroup: moduleOpts.toggleGroup,
              options: moduleOpts.options
            });
          }
        }
        return modulePanels;
      },
      /**
       * Determines the module window configuration objects from app-config:
       *     moduleWins: [
       *       {type: 'wgu-layerlist-win'},
       *       {type: 'wgu-measuretool-win'}
       *     ]
       * @return {Array} module window configuration objects
       */
      getModuleWinData () {
        const appConfig = Vue.prototype.$appConfig || {};
        const modulesConfs = appConfig.modules || {};
        let moduleWins = [];
        for (const key of Object.keys(modulesConfs)) {
          const moduleOpts = appConfig.modules[key];
          if (moduleOpts.win === true) {
            moduleWins.push({
              type: key + '-win',
              dark: moduleOpts.darkLayout,
              draggable: moduleOpts.draggable,
              initPos: moduleOpts.initPos,
              active: moduleOpts.active,
              title: moduleOpts.title,
              width: moduleOpts.width,
              icon: moduleOpts.icon,
              options: moduleOpts.options
            });
          }
        }
        return moduleWins;
      }
    }
  }
</script>



// WEBPACK FOOTER //
// app/WguAppTemplate.vue